/**
 * Platform role keys — these must match the `role_key` values seeded in the
 * `auth_roles` table. Used for frontend role checks and the AI Feedback route
 * guard.
 */
export const RoleKeys = {
  PlatformAdmin: "ROLE_PLATFORM_ADMINISTRATOR",
  AiGovernanceManager: "ROLE_PLATFORM_AI_GOVERNANCE_MANAGER",
  ClientExecutive: "ROLE_CLIENT_EXECUTIVE_EXTERNAL",
  ClientContributor: "ROLE_CLIENT_CONTRIBUTOR_EXTERNAL",
  EngagementTeamMember: "ROLE_ENGAGEMENT_TEAM_MEMBER",
  OpsEngineerL1L2: "ROLE_OPS_ENGINEER_L1_L2",
  PlatformEngineerL3: "ROLE_PLATFORM_ENGINEER_L3",
} as const;

export type RoleKey = (typeof RoleKeys)[keyof typeof RoleKeys];

/** Roles that can access the AI Feedback admin dashboard. */
export const AI_FEEDBACK_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
  RoleKeys.PlatformEngineerL3,
];

/** Roles that can access user-access management surfaces. */
export const USER_ACCESS_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
];

/** Roles that can create, update, and remove user-access assignments. */
export const USER_ACCESS_MANAGE_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
];

/** Roles allowed to run or inspect tenant reset jobs. */
export const TENANT_RESET_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
];

/** Roles allowed to view deal/project documents. */
export const DOCUMENT_VIEW_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.ClientExecutive,
  RoleKeys.ClientContributor,
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
];

/** Roles allowed to upload/create document content. */
export const DOCUMENT_CREATE_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
];

/** Roles allowed to poll planning-readiness state. */
export const PLANNING_VIEW_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.ClientExecutive,
  RoleKeys.ClientContributor,
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
];

/** Roles allowed to create and update deal data. */
export const DEAL_MANAGE_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
];

/** Roles allowed to access deal onboarding creation/resume surfaces. */
export const DEAL_ONBOARDING_ALLOWED_ROLES: readonly string[] = DEAL_MANAGE_ALLOWED_ROLES;

/** Roles allowed to access RAID, dependencies, and decisions surfaces. */
export const RAID_VIEW_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.ClientExecutive,
  RoleKeys.ClientContributor,
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
];

/** Roles allowed to manage prompt templates and versions. */
export const PROMPT_VIEW_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
  RoleKeys.PlatformEngineerL3,
];

/** Roles allowed to access chatbot entry points. */
export const CHATBOT_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.ClientExecutive,
  RoleKeys.ClientContributor,
  RoleKeys.EngagementTeamMember,
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
];

/**
 * Roles that may run the engagement team's approval of critical gap requirements.
 * Mirrors the backend grant of `CriticalGapRequirementReview.Approve` that the approval
 * endpoints require — a mismatch here just hides a page the API would have allowed, or
 * shows one every call 403s on.
 */
export const CRITICAL_GAP_APPROVAL_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
  RoleKeys.EngagementTeamMember,
];

/**
 * Roles that may inspect AI workflow traces.
 *
 * Mirrors the backend grant of `WorkflowTrace.View`. Traces hold the raw,
 * unredacted prompts and model responses for a run, which is why this is a
 * narrower set than the roles allowed to *start* a workflow.
 */
export const WORKFLOW_TRACE_ALLOWED_ROLES: readonly string[] = [
  RoleKeys.PlatformAdmin,
  RoleKeys.AiGovernanceManager,
  RoleKeys.PlatformEngineerL3,
];

/**
 * Roles that may reset a deal's gap analysis on a non-production environment.
 *
 * Mirrors the backend grant of `CriticalGapRun.Reset`. Deliberately narrower than
 * `TenantReset.Run`, which ops and platform engineers also hold: this destroys an
 * engagement team's recorded triage decisions, not just generated output.
 */
export const GAP_ANALYSIS_RESET_ALLOWED_ROLES: readonly string[] = [RoleKeys.PlatformAdmin];
